"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"

import { TypingIndicator } from "@/components/ui/typing-indicator"

/**
 * Staged wait copy, so a slow answer reads as work in progress rather than a hang.
 *
 * The stages run on elapsed time, not on what the agent is doing — nothing reports real
 * progress until the reply lands. So they stay vague, and the lookup wording appears only
 * where a lookup is at least possible. What the agent actually did is shown afterwards,
 * from the real tool calls.
 */
type Phase = { label: keyof typeof import("@/translations/en/agent-tools.json").AgentTools.ThinkingStatus; after: number }

// These are catalog keys, not display strings. Keep the timer arrays stable across locale changes.
const BASE_PHASES: Phase[] = [
  {label: "thinking", after: 0},
  {label: "puttingAnAnswerTogether", after: 2_500},
  {label: "stillWorking", after: 12_000},
]
const TOOL_PHASES: Phase[] = [
  {label: "thinking", after: 0},
  {label: "lookingThingsUp", after: 2_500},
  {label: "puttingAnAnswerTogether", after: 6_000},
  {label: "stillWorking", after: 15_000},
]

export function ThinkingStatus({ withTools = false }: { withTools?: boolean }) {
  const tUi = useTranslations("AgentTools.ThinkingStatus")

  const phases = withTools ? TOOL_PHASES : BASE_PHASES
  const [phaseIndex, setPhaseIndex] = useState(0)

  useEffect(() => {
    setPhaseIndex(0)
    const timers = phases
      .slice(1)
      .map((phase, index) => setTimeout(() => setPhaseIndex(index + 1), phase.after))
    return () => timers.forEach(clearTimeout)
  }, [phases])

  const label = tUi(phases[phaseIndex].label)

  return (
    <span
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 text-xs text-gray-400"
    >
      <TypingIndicator className="text-gray-300" />
      <span key={label} className="animate-in fade-in duration-500">
        {label}
      </span>
    </span>
  )
}
